import type {
  AmortizationSystem,
  BankConfig,
  BankSimulation,
  Eligibility,
  EligibilityReasonCode,
  FinancingResult,
  Installment,
  SimulationInput
} from "../../shared/contracts.js";

const MONEY_PRECISION = 100;
const EPSILON = 0.000001;

export function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * MONEY_PRECISION) / MONEY_PRECISION;
}

export function effectiveAnnualToMonthly(annualRatePct: number): number {
  if (annualRatePct === 0) return 0;
  return Math.pow(1 + annualRatePct / 100, 1 / 12) - 1;
}

export function pricePayment(principal: number, monthlyRate: number, months: number): number {
  if (months <= 0) throw new Error("O prazo deve ser maior que zero.");
  if (monthlyRate === 0) return principal / months;
  const factor = Math.pow(1 + monthlyRate, months);
  return principal * ((monthlyRate * factor) / (factor - 1));
}

function buildPriceSchedule(
  principal: number,
  monthlyRate: number,
  months: number,
  bank: BankConfig
): Installment[] {
  const fixedBasePayment = pricePayment(principal, monthlyRate, months);
  const insuranceRate = bank.monthlyInsuranceRatePct / 100;
  const schedule: Installment[] = [];
  let balance = principal;

  for (let month = 1; month <= months; month += 1) {
    const openingBalance = balance;
    const interest = openingBalance * monthlyRate;
    const plannedAmortization = fixedBasePayment - interest;
    const amortization = month === months ? openingBalance : Math.min(openingBalance, plannedAmortization);
    const basePayment = amortization + interest;
    const insurance = openingBalance * insuranceRate;
    balance = Math.max(0, openingBalance - amortization);

    schedule.push({
      month,
      openingBalance: roundMoney(openingBalance),
      amortization: roundMoney(amortization),
      interest: roundMoney(interest),
      insurance: roundMoney(insurance),
      adminFee: roundMoney(bank.monthlyAdminFee),
      basePayment: roundMoney(basePayment),
      totalPayment: roundMoney(basePayment + insurance + bank.monthlyAdminFee),
      closingBalance: roundMoney(balance)
    });
  }

  return schedule;
}

function buildSacSchedule(
  principal: number,
  monthlyRate: number,
  months: number,
  bank: BankConfig
): Installment[] {
  const constantAmortization = principal / months;
  const insuranceRate = bank.monthlyInsuranceRatePct / 100;
  const schedule: Installment[] = [];
  let balance = principal;

  for (let month = 1; month <= months; month += 1) {
    const openingBalance = balance;
    const amortization =
      month === months ? openingBalance : Math.min(openingBalance, constantAmortization);
    const interest = openingBalance * monthlyRate;
    const basePayment = amortization + interest;
    const insurance = openingBalance * insuranceRate;
    balance = Math.max(0, openingBalance - amortization);

    schedule.push({
      month,
      openingBalance: roundMoney(openingBalance),
      amortization: roundMoney(amortization),
      interest: roundMoney(interest),
      insurance: roundMoney(insurance),
      adminFee: roundMoney(bank.monthlyAdminFee),
      basePayment: roundMoney(basePayment),
      totalPayment: roundMoney(basePayment + insurance + bank.monthlyAdminFee),
      closingBalance: roundMoney(balance)
    });
  }

  return schedule;
}

function netPresentValue(netProceeds: number, payments: number[], monthlyRate: number): number {
  return (
    netProceeds -
    payments.reduce(
      (presentValue, payment, index) =>
        presentValue + payment / Math.pow(1 + monthlyRate, index + 1),
      0
    )
  );
}

export function estimateCetAnnual(
  financedAmount: number,
  upfrontFee: number,
  payments: number[]
): number | null {
  const netProceeds = financedAmount - upfrontFee;
  if (netProceeds <= 0 || payments.length === 0) return null;

  const atZero = netPresentValue(netProceeds, payments, 0);
  if (atZero >= 0) return 0;

  let low = 0;
  let high = 1;
  for (let iteration = 0; iteration < 100; iteration += 1) {
    const middle = (low + high) / 2;
    const npv = netPresentValue(netProceeds, payments, middle);
    if (Math.abs(npv) < EPSILON) {
      low = middle;
      high = middle;
      break;
    }
    if (npv > 0) high = middle;
    else low = middle;
  }

  const monthly = (low + high) / 2;
  return (Math.pow(1 + monthly, 12) - 1) * 100;
}

function evaluateEligibility(
  input: SimulationInput,
  bank: BankConfig,
  firstPayment: number,
  financedAmount: number
): Eligibility {
  const reasons: string[] = [];
  const reasonCodes: EligibilityReasonCode[] = [];
  const maximumFinancing = input.propertyValue * (bank.maxLtvPct / 100);
  const ageAtEnd = input.age + input.termMonths / 12;
  const maximumPayment = input.monthlyIncome * (bank.incomeCommitmentPct / 100);

  if (financedAmount > maximumFinancing + 0.01) {
    reasons.push(`Entrada abaixo da quota mínima de ${100 - bank.maxLtvPct}%.`);
    reasonCodes.push("ltv");
  }
  if (input.termMonths > bank.maxTermMonths) {
    reasons.push(`Prazo acima do limite de ${Math.floor(bank.maxTermMonths / 12)} anos.`);
    reasonCodes.push("term");
  }
  if (ageAtEnd > bank.maxAgeAtEnd) {
    reasons.push(`Idade ao final acima do limite de ${bank.maxAgeAtEnd} anos.`);
    reasonCodes.push("age-at-end");
  }
  if (firstPayment > maximumPayment + 0.01) {
    reasons.push(`Parcela supera ${bank.incomeCommitmentPct}% da renda informada.`);
    reasonCodes.push("income-commitment");
  }

  return {
    eligible: reasons.length === 0,
    reasons,
    reasonCodes,
    incomeUsagePct: input.monthlyIncome > 0 ? (firstPayment / input.monthlyIncome) * 100 : 100,
    minimumIncome: firstPayment / (bank.incomeCommitmentPct / 100)
  };
}

function resultFromSchedule(
  input: SimulationInput,
  bank: BankConfig,
  system: AmortizationSystem,
  annualRate: number,
  schedule: Installment[]
): FinancingResult {
  const financedAmount = input.propertyValue - input.downPayment;
  const totalInterest = schedule.reduce((sum, item) => sum + item.interest, 0);
  const totalInsurance = schedule.reduce((sum, item) => sum + item.insurance, 0);
  const totalAdminFees = schedule.reduce((sum, item) => sum + item.adminFee, 0);
  const payments = schedule.map((item) => item.totalPayment);
  const totalPaid = payments.reduce((sum, payment) => sum + payment, bank.upfrontFee);
  const firstPayment = schedule[0]?.totalPayment ?? 0;
  const lastPayment = schedule.at(-1)?.totalPayment ?? 0;
  const roundedFinancedAmount = roundMoney(financedAmount);
  const roundedTotalInterest = roundMoney(totalInterest);
  const roundedTotalInsurance = roundMoney(totalInsurance);
  const roundedTotalAdminFees = roundMoney(totalAdminFees);
  const roundedUpfrontFee = roundMoney(bank.upfrontFee);
  const roundedTotalPaid = roundMoney(totalPaid);
  const roundingAdjustment = roundMoney(
    roundedTotalPaid -
      (roundedFinancedAmount +
        roundedTotalInterest +
        roundedTotalInsurance +
        roundedTotalAdminFees +
        roundedUpfrontFee)
  );

  return {
    key: `${bank.id}-${system}`,
    bankId: bank.id,
    bankName: bank.name,
    bankShortName: bank.shortName,
    accent: bank.accent,
    system,
    annualRate,
    monthlyRate: effectiveAnnualToMonthly(annualRate),
    financedAmount: roundedFinancedAmount,
    firstPayment: roundMoney(firstPayment),
    lastPayment: roundMoney(lastPayment),
    averagePayment: roundMoney(payments.reduce((sum, item) => sum + item, 0) / payments.length),
    totalInterest: roundedTotalInterest,
    totalInsurance: roundedTotalInsurance,
    totalAdminFees: roundedTotalAdminFees,
    upfrontFee: roundedUpfrontFee,
    roundingAdjustment,
    totalPaid: roundedTotalPaid,
    estimatedCetAnnual: estimateCetAnnual(financedAmount, bank.upfrontFee, payments),
    eligibility: evaluateEligibility(input, bank, firstPayment, financedAmount),
    schedule
  };
}

export function simulateBank(input: SimulationInput, bank: BankConfig): BankSimulation {
  const principal = input.propertyValue - input.downPayment;
  if (principal <= 0) throw new Error("O valor financiado deve ser maior que zero.");

  const sacMonthlyRate = effectiveAnnualToMonthly(bank.effectiveAnnualRateSac);
  const priceMonthlyRate = effectiveAnnualToMonthly(bank.effectiveAnnualRatePrice);
  const sacSchedule = buildSacSchedule(principal, sacMonthlyRate, input.termMonths, bank);
  const priceSchedule = buildPriceSchedule(principal, priceMonthlyRate, input.termMonths, bank);

  return {
    bank,
    sac: resultFromSchedule(
      input,
      bank,
      "sac",
      bank.effectiveAnnualRateSac,
      sacSchedule
    ),
    price: resultFromSchedule(
      input,
      bank,
      "price",
      bank.effectiveAnnualRatePrice,
      priceSchedule
    )
  };
}

export function simulateAll(input: SimulationInput, banks: BankConfig[]): BankSimulation[] {
  return banks.filter((bank) => bank.enabled).map((bank) => simulateBank(input, bank));
}

export function flattenResults(simulations: BankSimulation[]): FinancingResult[] {
  return simulations.flatMap((simulation) => [simulation.sac, simulation.price]);
}

export function rankResults(results: FinancingResult[]): FinancingResult[] {
  return [...results].sort((left, right) => {
    if (left.eligibility.eligible !== right.eligibility.eligible) {
      return left.eligibility.eligible ? -1 : 1;
    }
    return left.totalPaid - right.totalPaid;
  });
}

export function annualizeSchedule(schedule: Installment[]): Installment[] {
  const years: Installment[] = [];
  for (let index = 0; index < schedule.length; index += 12) {
    const period = schedule.slice(index, index + 12);
    const first = period[0];
    const last = period.at(-1);
    if (!first || !last) continue;
    years.push({
      month: Math.ceil((index + 1) / 12),
      openingBalance: first.openingBalance,
      amortization: roundMoney(period.reduce((sum, item) => sum + item.amortization, 0)),
      interest: roundMoney(period.reduce((sum, item) => sum + item.interest, 0)),
      insurance: roundMoney(period.reduce((sum, item) => sum + item.insurance, 0)),
      adminFee: roundMoney(period.reduce((sum, item) => sum + item.adminFee, 0)),
      basePayment: roundMoney(period.reduce((sum, item) => sum + item.basePayment, 0)),
      totalPayment: roundMoney(period.reduce((sum, item) => sum + item.totalPayment, 0)),
      closingBalance: last.closingBalance
    });
  }
  return years;
}
